/**
 * 指数清单 —— 全应用唯一数据源（需求 §F1）
 *
 * 13 个指数的元信息集中于此，行情卡片 / 图表页头部 / 顶栏快切 / 搜索
 * 全部从这里读，避免同一指数在多处各写一份而逐渐不一致。
 *
 * 代码格式与需求 §附录「数据源接口速查」一致：
 *   klineSymbol   腾讯 / 新浪：sh000001 · sz399006 · hkHSTECH · bj899050
 *   emSecid       东财：1.000001（沪）· 0.399006（深）· 2.932000（中证）· 124.HSTECH（港）
 */

export type Market = 'CN' | 'HK';

export interface Instrument {
  /** 主键，与需求 §4.3 instruments.code 一致 */
  code: string;
  name: string;
  market: Market;
  /** 腾讯/新浪代码 */
  klineSymbol: string;
  /** 东财 secid；无支持时为 null（如中证2000 仅东财可用但会限流） */
  emSecid: string | null;
  /** 中证官网估值代码；仅中证系 8 指数可用 */
  csindexCode: string | null;
  /** 首页默认排序 */
  sortOrder: number;
}

export const INSTRUMENTS: readonly Instrument[] = [
  { code: '000001', name: '上证指数', market: 'CN', klineSymbol: 'sh000001', emSecid: '1.000001', csindexCode: '000001', sortOrder: 1 },
  { code: '399001', name: '深证成指', market: 'CN', klineSymbol: 'sz399001', emSecid: '0.399001', csindexCode: null, sortOrder: 2 },
  { code: '399006', name: '创业板指', market: 'CN', klineSymbol: 'sz399006', emSecid: '0.399006', csindexCode: null, sortOrder: 3 },
  { code: '000300', name: '沪深300', market: 'CN', klineSymbol: 'sh000300', emSecid: '1.000300', csindexCode: '000300', sortOrder: 4 },
  { code: '000016', name: '上证50', market: 'CN', klineSymbol: 'sh000016', emSecid: '1.000016', csindexCode: '000016', sortOrder: 5 },
  { code: '000905', name: '中证500', market: 'CN', klineSymbol: 'sh000905', emSecid: '1.000905', csindexCode: '000905', sortOrder: 6 },
  { code: '000688', name: '科创50', market: 'CN', klineSymbol: 'sh000688', emSecid: '1.000688', csindexCode: '000688', sortOrder: 7 },
  { code: '000852', name: '中证1000', market: 'CN', klineSymbol: 'sh000852', emSecid: '1.000852', csindexCode: '000852', sortOrder: 8 },
  { code: '899050', name: '北证50', market: 'CN', klineSymbol: 'bj899050', emSecid: '0.899050', csindexCode: null, sortOrder: 9 },
  { code: '932000', name: '中证2000', market: 'CN', klineSymbol: 'sh932000', emSecid: '2.932000', csindexCode: '932000', sortOrder: 10 },
  { code: 'HSTECH', name: '恒生科技', market: 'HK', klineSymbol: 'hkHSTECH', emSecid: '124.HSTECH', csindexCode: null, sortOrder: 11 },
  { code: 'HSI', name: '恒生指数', market: 'HK', klineSymbol: 'hkHSI', emSecid: '100.HSI', csindexCode: null, sortOrder: 12 },
  { code: 'HSCEI', name: '国企指数', market: 'HK', klineSymbol: 'hkHSCEI', emSecid: '124.HSCEI', csindexCode: null, sortOrder: 13 },
] as const;

/** 仅东财支持、且东财封禁时该指数不可用（需求 §七 限制 1） */
export const EASTMONEY_ONLY = new Set(['932000']);

export const findInstrument = (code: string): Instrument | undefined =>
  INSTRUMENTS.find((i) => i.code === code);

export const DEFAULT_CODE = INSTRUMENTS[0].code;

/**
 * 顶栏快切默认展示的指数。
 * 用户在行情页选中的指数若不在此列，仍需出现在快切首位并高亮 ——
 * 否则会出现「图表页显示某指数、顶栏却无一高亮」的割裂感。
 * UI-B 接入偏好配置后可改为用户自定义。
 */
export const QUICK_SWITCH_CODES = ['000001', '399001', '399006', '000300', '000688', '000852', 'HSTECH'] as const;

/** 快切渲染顺序：选中指数优先，其余按默认序 */
export function buildQuickSwitch(code: string): string[] {
  const base = QUICK_SWITCH_CODES as readonly string[];
  return base.includes(code) ? [...base] : [code, ...base];
}